import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Banknote, Clock } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface ValidationResponse {
  valid: boolean;
  message: string;
  conflicts: string[];
}

interface PayTypeRateStepProps {
  employeeId: number;
  values: {
    payType: 'salary' | 'hourly' | null;
    payRate: string | null;
    salaryUnit: 'YEARLY' | 'MONTHLY' | 'WEEKLY' | null;
    payFrequency: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null;
  };
  workingHours: Record<string, string> | null;
  onChange: (updates: Partial<PayTypeRateStepProps['values']>) => void;
  onValidation: (isValid: boolean) => void;
}

export const PayTypeRateStep: React.FC<PayTypeRateStepProps> = ({
  employeeId,
  values,
  workingHours,
  onChange,
  onValidation
}) => {
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    message: string;
    conflicts: string[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const { payType, payRate, salaryUnit, payFrequency } = values;
    if (payType && payRate && salaryUnit && payFrequency) {
      // Only validate if we're not already validating and values have actually changed
      if (!isValidating) {
        // Debounce validation to prevent excessive API calls
        const timeoutId = setTimeout(() => {
          validatePayConfiguration();
        }, 500);
        
        return () => clearTimeout(timeoutId);
      }
    } else {
      setValidationResult(null);
      onValidation(false);
    }
  }, [values.payType, values.payRate, values.salaryUnit, values.payFrequency]);

  const validatePayConfiguration = async () => {
    const { payType, payRate, salaryUnit, payFrequency } = values;
    
    if (!payType || !payRate || !salaryUnit || !payFrequency) return;

    setIsValidating(true);
    try {
      const response = await ApiService.request<ValidationResponse>(
        EndpointKeys.VALIDATE_SETUP_STEP,
        {
          method: "POST",
          urlParams: { employeeId },
          body: {
            step: 'pay',
            pay_type: payType,
            pay_rate: parseFloat(payRate),
            salary_unit: salaryUnit,
            pay_frequency: payFrequency
          }
        }
      );

      const result = {
        valid: response.valid,
        message: response.message,
        conflicts: response.conflicts || []
      };

      setValidationResult(result);
      onValidation(result.valid);
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({
        valid: false,
        message: 'Failed to validate pay configuration',
        conflicts: ['Validation service unavailable']
      });
      onValidation(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handlePayTypeChange = (payType: 'salary' | 'hourly') => {
    // Reset dependent fields when pay type changes
    // Always set pay frequency to MONTHLY as that's the only supported option
    const updates: Partial<PayTypeRateStepProps['values']> = {
      payType,
      salaryUnit: payType === 'salary' ? 'YEARLY' : 'WEEKLY',
      payFrequency: 'MONTHLY'
    };
    onChange(updates);
  };

  const getSalaryUnitOptions = () => {
    if (values.payType === 'salary') {
      return [
        { value: 'YEARLY', label: 'Per year' },
        { value: 'MONTHLY', label: 'Per month' },
      ];
    } else {
      return [
        { value: 'WEEKLY', label: 'Per week' },
      ];
    }
  };


  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return isNaN(num) ? '£0' : `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  const getPayRateLabel = () => {
    if (!values.payType) return 'Pay Rate';
    return values.payType === 'salary' ? 'Salary Amount' : 'Hourly Rate';
  };

  const getPayRatePlaceholder = () => {
    if (!values.payType) return 'Enter amount';
    return values.payType === 'salary' ? 'e.g., 35000' : 'e.g., 12.50';
  };

  const calculateEquivalent = () => {
    const rate = parseFloat(values.payRate || '0');
    if (isNaN(rate) || rate <= 0) return null;

    // Calculate total weekly hours from working hours data
    const weeklyHours = workingHours 
      ? Object.values(workingHours).reduce((total, hours) => {
          return total + (parseFloat(hours || '0') || 0);
        }, 0)
      : 37.5; // Default fallback if no working hours set yet

    if (values.payType === 'salary' && values.salaryUnit) {
      // Convert salary to hourly based on actual working hours
      let annualSalary = rate;
      if (values.salaryUnit === 'MONTHLY') {
        annualSalary = rate * 12;
      } else if (values.salaryUnit === 'WEEKLY') {
        annualSalary = rate * 52;
      } else if (values.salaryUnit === 'YEARLY') {
        annualSalary = rate;
      }
      const hourlyEquivalent = annualSalary / (weeklyHours * 52);
      return `Approximately £${hourlyEquivalent.toFixed(2)}/hour (based on ${weeklyHours} hours/week)`;
    } else if (values.payType === 'hourly') {
      // Convert hourly to annual based on actual working hours
      const annualEquivalent = rate * weeklyHours * 52;
      return `Approximately £${annualEquivalent.toLocaleString('en-GB')}/year (based on ${weeklyHours} hours/week)`;
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* Pay Type Selection */}
      <div className="space-y-4">
        <RadioGroup
          value={values.payType || ''}
          onValueChange={(value) => handlePayTypeChange(value as 'salary' | 'hourly')}
          className="grid grid-cols-2 gap-4 p-1"
        >
          <Card 
            className={`cursor-pointer transition-colors ${
              values.payType === 'salary' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
            }`}
            onClick={() => handlePayTypeChange('salary')}
          >
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="salary" id="salary" />
                <Label htmlFor="salary" className="flex items-center cursor-pointer">
                  <Banknote className="h-4 w-4 mr-2" />
                  Salary
                </Label>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Fixed annual amount, typically paid monthly
              </p>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-colors ${
              values.payType === 'hourly' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
            }`}
            onClick={() => handlePayTypeChange('hourly')}
          >
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hourly" id="hourly" />
                <Label htmlFor="hourly" className="flex items-center cursor-pointer">
                  <Clock className="h-4 w-4 mr-2" />
                  Hourly
                </Label>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Rate per hour worked, varies with hours
              </p>
            </CardContent>
          </Card>
        </RadioGroup>
      </div>

      {/* Pay Rate Input */}
      {values.payType && (
        <div className="space-y-2 px-1">
          <Label htmlFor="pay-rate">{getPayRateLabel()}</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">£</span>
            <Input
              id="pay-rate"
              type="number"
              step="0.01"
              min="0"
              placeholder={getPayRatePlaceholder()}
              value={values.payRate || ''}
              onChange={(e) => onChange({ payRate: e.target.value })}
              className="pl-8"
            />
          </div>
          {values.payRate && (
            <p className="text-sm text-gray-600">
              {formatCurrency(values.payRate)} {values.salaryUnit?.toLowerCase()}
            </p>
          )}
        </div>
      )}

      {/* Salary Unit (for salary type) */}
      {values.payType === 'salary' && (
        <div className="space-y-2">
          <Label>Salary Period</Label>
          <Select
            value={values.salaryUnit || ''}
            onValueChange={(value) => onChange({ salaryUnit: value as 'YEARLY' | 'MONTHLY' | 'WEEKLY' })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select salary period" />
            </SelectTrigger>
            <SelectContent>
              {getSalaryUnitOptions().map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Pay Frequency Information */}
      {values.payType && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <Label className="text-sm font-medium text-blue-900">Pay Frequency</Label>
          <p className="text-sm text-blue-800 mt-1">
            All employees are paid monthly. This ensures consistent payroll processing and compliance with UK employment standards.
          </p>
        </div>
      )}

      {/* Equivalent Rate Display */}
      {calculateEquivalent() && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Equivalent rate:</strong> {calculateEquivalent()}
          </p>
        </div>
      )}

      {/* Validation Status */}
      {isValidating && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Validating pay configuration...
          </AlertDescription>
        </Alert>
      )}

      {validationResult && !isValidating && (
        <Alert className={validationResult.valid ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
          {validationResult.valid ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription className={validationResult.valid ? "text-green-800" : "text-red-800"}>
            <div>
              <p className="font-medium">{validationResult.message}</p>
              {validationResult.conflicts.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-sm">
                  {validationResult.conflicts.map((conflict, index) => (
                    <li key={index}>{conflict}</li>
                  ))}
                </ul>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* UK Minimum Wage Information */}
      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
        <h4 className="font-medium text-amber-900 mb-2">UK Minimum Wage (2025):</h4>
        <ul className="text-sm text-amber-800 space-y-1">
          <li>• National Living Wage (21+): £12.21/hour</li>
          <li>• 18-20 years: £10.00/hour</li>
          <li>• 16-17 years: £7.55/hour</li>
          <li>• Apprentice Rate: £7.55/hour</li>
        </ul>
      </div>
    </div>
  );
};
